import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Alert, CircularProgress,
} from '@mui/material';
import axios from 'axios';
import API_BASE_URL from '../config';
import { getErrorMessage } from '../utils/apiError';
import useAccess from '../hooks/useAccess';

const FEATURES = [
  '✅ Full profile view',
  '✅ Unlimited search',
  '✅ Unlimited profile edits',
  '✅ Photo gallery access',
  '✅ Comments on profiles',
  '✅ Profile visitor list',
  '✅ Lifetime access — no renewal',
];

const UpgradeModal = ({ open, onClose, featureLabel }) => {
  const { userId } = useAccess();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setPaying(false);
      setError('');
      setSuccess(false);
    }
  }, [open]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // const handleUpgrade = async () => {
  //   setError('');
  //   setPaying(true);

  //   try {
  //     const scriptLoaded = await loadRazorpayScript();
  //     if (!scriptLoaded) throw new Error('Razorpay SDK failed to load');

  //     const orderRes = await axios.post(
  //       `${API_BASE_URL}/create-order`,
  //       {},
  //       { withCredentials: true }
  //     );

  //     const { order_id, amount, currency, key } = orderRes.data;
  //     if (!order_id) throw new Error('Order creation failed');

  //     const options = {
  //       key,
  //       amount,
  //       currency,
  //       name: "Gold Petals",
  //       description: "Premium Plan Upgrade",
  //       order_id: order_id,
  //       handler: async (response) => {
  //         try {
  //           await axios.post(
  //             `${API_BASE_URL}/verify-payment`,
  //             {
  //               order_id: response.razorpay_order_id,
  //               payment_id: response.razorpay_payment_id,
  //               signature: response.razorpay_signature
  //             },
  //             { withCredentials: true }
  //           );
            
  //           window.dispatchEvent(new Event('planUpdated'));
  //           setSuccess(true);
  //           setTimeout(() => onClose(), 1500);
  //         } catch (vErr) {
  //           setError(getErrorMessage(vErr));
  //         } finally {
  //           setPaying(false);
  //         }
  //       },
  //       theme: { color: "#1976d2" },
  //       modal: { ondismiss: () => setPaying(false) }
  //     };

  //     const rzp = new window.Razorpay(options);
  //     rzp.open();

  //   } catch (err) {
  //     if (err.response?.status === 401) {
  //       window.location.href = '/goldpetals/login';
  //     } else {
  //       setError(getErrorMessage(err));
  //     }
  //   } finally {
  //     setPaying(false);
  //   }
  // };

const handleUpgrade = async () => {
  if (paying) return;

  setError('');
  setPaying(true);

  try {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) throw new Error('Razorpay SDK failed to load');

    const orderRes = await axios.post(
      `${API_BASE_URL}/create-order`,
      {},
      { withCredentials: true }
    );

    const { order_id, amount, currency, key } = orderRes.data;
    if (!order_id) throw new Error('Order creation failed');

    const options = {
      key,
      amount,
      currency,
      name: "Gold Petals",
      description: "Premium Plan Upgrade",
      order_id,

      handler: async (response) => {
        try {
          await axios.post(
            `${API_BASE_URL}/verify-payment`,
            {
              order_id: response.razorpay_order_id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature
            },
            { withCredentials: true }
          );

              // 🔥 GLOBAL SYNC: Fire both events to satisfy Hook + Sidebar triggers
          window.dispatchEvent(new Event('planUpdated'));
          window.dispatchEvent(new Event('goldpetals:access-refresh'));
          
          setSuccess(true);
          setTimeout(() => onClose(), 1500);

        } catch (vErr) {
          setError(getErrorMessage(vErr));
        } finally {
          setPaying(false);
        }
      },

      modal: {
        ondismiss: () => setPaying(false)
      },

      theme: { color: "#1976d2" }
    };

    const rzp = new window.Razorpay(options);

    // ✅ IMPORTANT FIX
    rzp.on('payment.failed', function () {
      setError('Payment failed. Please try again.');
      setPaying(false);
    });

    rzp.open();

  } catch (err) {
    if (err.response?.status === 401) {
      window.location.href = '/goldpetals/login';
    } else {
      setError(getErrorMessage(err));
    }
    setPaying(false);
  }
};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: 3, minWidth: 340, p: 1 } }}
    >
      <DialogTitle
        sx={{
          background: (t) =>
            `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
          color: 'white',
          fontWeight: 'bold',
          textAlign: 'center',
          borderRadius: '12px 12px 0 0',
        }}
      >
        ⭐ Upgrade to Premium
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 1, textAlign: 'center' }}>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Payment successful. Premium activated!
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {featureLabel && (
          <Typography variant="body1" fontWeight={600} gutterBottom>
            "{featureLabel}" requires a Premium plan.
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upgrade once for ₹500 — lifetime access to:
        </Typography>

        {FEATURES.map((f, i) => (
          <Typography key={i} variant="body2" sx={{ textAlign: 'left', mb: 0.5 }}>
            {f}
          </Typography>
        ))}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ borderRadius: 2 }}
          disabled={paying}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleUpgrade}
          disabled={paying || success}
          startIcon={paying ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{
            borderRadius: 2,
            background: (t) =>
              `linear-gradient(45deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
          }}
        >
          {paying ? 'Processing...' : 'Upgrade Now — ₹500'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeModal;